import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package } from 'lucide-react';
import { equipmentBookingAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: { color: '#D97706', bg: '#FFFBEB' },
  confirmed: { color: '#059669', bg: '#ECFDF5' },
  picked_up: { color: '#2563EB', bg: '#EFF6FF' },
  returned: { color: '#6B7280', bg: '#F3F4F6' },
  cancelled: { color: '#DC2626', bg: '#FEF2F2' },
  overdue: { color: '#DC2626', bg: '#FFF1F2' },
  damaged: { color: '#9333EA', bg: '#FAF5FF' },
};

const EquipmentBookingsPage = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

  const fetchBookings = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const res = await equipmentBookingAPI.getAll(params);
      setBookings(res.data.data || []);
      setPagination(res.data.pagination || {});
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [statusFilter]);

  const updateStatus = async (id, status, condition) => {
    try {
      await equipmentBookingAPI.updateStatus(id, { status, condition });
      toast.success(`Marked as ${status.replace('_', ' ')}`);
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.bookingNumber?.toLowerCase().includes(q) ||
      b.equipment?.name?.toLowerCase().includes(q) ||
      b.guest?.firstName?.toLowerCase().includes(q) ||
      b.guest?.lastName?.toLowerCase().includes(q)
    );
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Equipment Bookings</h1>
          <p className="page-subtitle">{pagination.total} total bookings</p>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/equipment/operations')}>
          ⚙ Operations Dashboard
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-bar">
          <Search size={16} />
          <input placeholder="Search by booking #, equipment, guest..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 180 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {Object.keys(STATUS_STYLES).map(s => (
            <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Booking #</th>
                  <th>Equipment</th>
                  <th>Guest</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Qty</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="empty-state">
                        <Package size={40} />
                        <h3>No bookings found</h3>
                      </div>
                    </td>
                  </tr>
                ) : filtered.map(b => (
                  <tr key={b._id}>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, background: '#F3F4F6', padding: '2px 8px', borderRadius: 4 }}>
                        {b.bookingNumber}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{b.equipment?.name}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>{b.equipment?.category?.replace('_', ' ')}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{b.guest?.firstName} {b.guest?.lastName}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF' }}>{b.guest?.phone}</div>
                    </td>
                    <td>{b.startDate ? format(new Date(b.startDate), 'MMM d, yyyy') : '—'}</td>
                    <td>{b.endDate ? format(new Date(b.endDate), 'MMM d, yyyy') : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{b.quantity}</td>
                    <td style={{ fontWeight: 600 }}>${b.pricing?.total?.toLocaleString()}</td>
                    <td>
                      <span className="badge" style={{ color: STATUS_STYLES[b.status]?.color, background: STATUS_STYLES[b.status]?.bg }}>
                        {b.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        {b.status === 'confirmed' && (
                          <button className="btn btn-sm btn-primary" onClick={() => updateStatus(b._id, 'picked_up')}>
                            Picked Up
                          </button>
                        )}
                        {b.status === 'picked_up' && (
                          <button className="btn btn-sm btn-success" onClick={() => updateStatus(b._id, 'returned', 'good')}>
                            Returned
                          </button>
                        )}
                        {b.status === 'overdue' && (
                          <span style={{ fontSize: 12, color: '#DC2626', fontWeight: 500 }}>⚠ Overdue</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
            <button key={p} className={`btn btn-sm ${pagination.page === p ? 'btn-primary' : 'btn-secondary'}`} onClick={() => fetchBookings(p)}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipmentBookingsPage;
